import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Crown, ChevronRight, Info, ArrowLeft, ChevronDown, ChevronUp, Gift, CheckCircle2 } from "lucide-react";
import { UsernameInput } from "../../components/UsernameInput";
import { getUsernameFromOtherSystem } from "../../services/usernameService";
import { useAuthContext } from "../../contexts/AuthContext";
import { auth } from "../../lib/firebase";

type BillingCycle = "monthly" | "semi_annual" | "yearly" | null;
type PlanType = "pro" | "pro_plus";

interface PackageSelectionState {
  planType: PlanType;
  billingCycle: BillingCycle;
  hasPathUrl: boolean;
  hasSubdomain: boolean;
  username: string;
}

export default function PackageSelection() {
  const navigate = useNavigate();
  const [existingUsername, setExistingUsername] = useState<string | null>(null);
  
  const urlParams = new URLSearchParams(window.location.search);
  const planParam = urlParams.get('plan');
  
  const getInitialPlanType = (): PlanType => {
    if (planParam === 'pro' || planParam === 'pro_plus') {
      return planParam;
    }
    return 'pro';
  };
  
  const [selection, setSelection] = useState<PackageSelectionState>({
    planType: getInitialPlanType(),
    billingCycle: null,
    hasPathUrl: false,
    hasSubdomain: false,
    username: ""
  });

  const [currentStep, setCurrentStep] = useState<0 | 1 | 2 | 3>(1);

  // ดึง username เดิมจาก SalePage (ถ้ามี)
  useEffect(() => {
    const fetchExistingUsername = async () => {
      const user = auth.currentUser;
      if (user?.uid) {
        const result = await getUsernameFromOtherSystem(user.uid, 'course');
        if (result.username) {
          setExistingUsername(result.username);
        }
      }
    };
    fetchExistingUsername();
  }, []);

  // Auto-select Subdomain เมื่อเลือก Pro+ รายปี (ได้ฟรี)
  useEffect(() => {
    const isProPlusYearly = selection.planType === "pro_plus" && selection.billingCycle === "yearly";
    if (isProPlusYearly && !selection.hasSubdomain) {
      setSelection(prev => ({ ...prev, hasSubdomain: true }));
    }
  }, [selection.planType, selection.billingCycle]);

  const handleContinue = () => {
    // Step 1: บังคับเลือกแพ็กเกจ
    if (!selection.planType) {
      alert('กรุณาเลือกแพ็กเกจใน Step 1');
      setCurrentStep(1);
      return;
    }

    // Step 2: บังคับเลือกรอบการชำระเงิน
    if (!selection.billingCycle) {
      alert('กรุณาเลือกรอบการชำระเงินใน Step 2');
      setCurrentStep(2);
      return;
    }

    // สร้าง tier parameter ตามแพ็คเก็จและรอบการชำระเงินที่เลือก
    const tierMap: Record<string, Record<string, string>> = {
      'pro': {
        'monthly': 'pro_monthly',
        'semi_annual': 'pro_semi_annual',
        'yearly': 'pro_yearly'
      },
      'pro_plus': {
        'monthly': 'pro_plus_monthly',
        'semi_annual': 'pro_plus_semi_annual',
        'yearly': 'pro_plus_yearly'
      }
    };
    
    const tier = tierMap[selection.planType][selection.billingCycle!];
    
    // Save checkout data with isNewPurchase flag
    const checkoutData = {
      isNewPurchase: true,
      tier: tier,
      planType: selection.planType,
      billingCycle: selection.billingCycle,
      source: 'edumi'
    };
    localStorage.setItem('subscriptionCheckout', JSON.stringify(checkoutData));
    
    navigate(`/subscription/checkout-new?tier=${tier}`);
  };

  const getBasePrice = () => {
    const cycle = selection.billingCycle || "monthly";
    
    if (selection.planType === "pro") {
      if (cycle === "monthly") return 690;
      if (cycle === "semi_annual") return 690 * 5; // 6 เดือน ฟรี 1 เดือน = จ่าย 5 เดือน
      return 690 * 10; // 1 ปี ฟรี 2 เดือน = จ่าย 10 เดือน
    } else {
      if (cycle === "monthly") return 1590;
      if (cycle === "semi_annual") return 1590 * 5; // 6 เดือน ฟรี 1 เดือน = จ่าย 5 เดือน
      return 1590 * 10; // 1 ปี ฟรี 2 เดือน = จ่าย 10 เดือน
    }
  };

  const getUrlAddonPrice = () => {
    const cycle = selection.billingCycle || "monthly";
    let total = 0;
    
    // Pro+ รายปีได้ Subdomain ฟรี
    const isProPlusYearly = selection.planType === "pro_plus" && cycle === "yearly";
    
    if (selection.hasPathUrl) {
      if (cycle === "monthly") total += 49;
      else if (cycle === "semi_annual") total += 49 * 5; // 6 เดือน ฟรี 1 เดือน = จ่าย 5 เดือน
      else total += 49 * 10; // 1 ปี ฟรี 2 เดือน = จ่าย 10 เดือน
    }
    
    if (selection.hasSubdomain && !isProPlusYearly) {
      if (cycle === "monthly") total += 99;
      else if (cycle === "semi_annual") total += 99 * 5; // 6 เดือน ฟรี 1 เดือน = จ่าย 5 เดือน
      else total += 99 * 10; // 1 ปี ฟรี 2 เดือน = จ่าย 10 เดือน
    }
    
    return total;
  };

  const getTotalPrice = () => {
    return getBasePrice() + getUrlAddonPrice();
  };

  const getSavings = () => {
    const baseMonthly = selection.planType === "pro" ? 690 : 1590;
    let addonMonthlySavings = 0;
    
    // คำนวณส่วนลดจาก URL Add-ons
    if (selection.hasPathUrl) {
      addonMonthlySavings += 49;
    }
    if (selection.hasSubdomain && !isProPlusYearly) {
      addonMonthlySavings += 99;
    }
    
    if (selection.billingCycle === "semi_annual") {
      return baseMonthly + addonMonthlySavings; // ฟรี 1 เดือน (base + addons)
    }
    if (selection.billingCycle === "yearly") {
      return (baseMonthly + addonMonthlySavings) * 2; // ฟรี 2 เดือน (base + addons)
    }
    return 0;
  };

  const isProPlusYearly = selection.planType === "pro_plus" && selection.billingCycle === "yearly";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-8 md:py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm md:text-base font-medium">กลับ</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-textDark mb-3">
            เลือกแพ็กเกจของคุณ
          </h1>
          <p className="text-base md:text-lg text-textLight">
            เลือกแพ็กเกจ รอบการชำระเงิน และ URL ที่คุณต้องการ
          </p>
        </div>

        {/* Main Layout: Steps + Sidebar Summary */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Steps */}
          <div className="flex-1 space-y-6">

        {/* Step 1: Choose Plan Type */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <button
            onClick={() => setCurrentStep(currentStep === 1 ? 0 : 1)}
            className="w-full p-4 md:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3 flex-1">
              <span className="w-8 h-8 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {selection.planType ? <Check className="w-5 h-5" /> : "1"}
              </span>
              <div className="text-left">
                <h2 className="text-lg md:text-xl font-bold text-textDark">Step 1: เลือกแพ็กเกจ</h2>
                {selection.planType && currentStep !== 1 && (
                  <p className="text-sm text-gray-600">
                    {selection.planType === "pro" ? "Pro" : "Pro+"} - ฿{selection.planType === "pro" ? "690" : "1,590"}/เดือน
                  </p>
                )}
              </div>
            </div>
            {currentStep === 1 ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          
          {currentStep === 1 && (
            <div className="p-4 md:p-6 pt-0 border-t">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Pro Plan */}
                <button
                  onClick={() => setSelection({ ...selection, planType: "pro" })}
                  className={`relative p-4 md:p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    selection.planType === "pro"
                      ? "border-blue-500 bg-blue-50 shadow-lg"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                >
                  {selection.planType === "pro" && (
                    <div className="absolute -top-3 -right-3 bg-blue-500 text-white p-2 rounded-full">
                      <Check className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                    <h3 className="text-xl md:text-2xl font-bold text-textDark">Pro</h3>
                  </div>
                  
                  <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-4">
                    ฿690<span className="text-xs md:text-sm text-textLight">/เดือน</span>
                  </div>

                  <ul className="space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>พื้นที่ 20 GB</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>ไม่จำกัดนักเรียน</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>สร้างคอร์สไม่จำกัด</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>Dashboard และ Analytics</span>
                    </li>
                  </ul>
                </button>

                {/* Pro+ Plan */}
                <button
                  onClick={() => setSelection({ ...selection, planType: "pro_plus" })}
                  className={`relative p-4 md:p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    selection.planType === "pro_plus"
                      ? "border-blue-500 bg-blue-50 shadow-lg"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                >
                  {selection.planType === "pro_plus" && (
                    <div className="absolute -top-3 -right-3 bg-blue-500 text-white p-2 rounded-full">
                      <Check className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                    <h3 className="text-xl md:text-2xl font-bold text-textDark">Pro+</h3>
                    <span className="text-[10px] md:text-xs bg-gradient-to-r from-blue-600 to-pink-600 text-white px-2 py-1 rounded-full font-bold">
                      Premium
                    </span>
                  </div>
                  
                  <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-4">
                    ฿1,590<span className="text-xs md:text-sm text-textLight">/เดือน</span>
                  </div>

                  <ul className="space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>พื้นที่ 30 GB</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>ไม่จำกัดนักเรียน</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>สร้างคอร์สไม่จำกัด</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                      <span>SEO Optimization</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-blue-600 flex-shrink-0" />
                      <span className="font-semibold text-blue-700">Subdomain ฟรี (รายปี)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-blue-600 flex-shrink-0" />
                      <span className="font-semibold text-blue-700">Salepage ฟรี (รายปี)</span>
                    </li>
                  </ul>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Choose Billing Cycle */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <button
            onClick={() => setCurrentStep(currentStep === 2 ? 0 : 2)}
            className="w-full p-4 md:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3 flex-1">
              <span className="w-8 h-8 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {selection.billingCycle ? <Check className="w-5 h-5" /> : "2"}
              </span>
              <div className="text-left">
                <h2 className="text-lg md:text-xl font-bold text-textDark">Step 2: เลือกรอบการชำระเงิน</h2>
                {selection.billingCycle && currentStep !== 2 && (
                  <p className="text-sm text-gray-600">
                    {selection.billingCycle === "monthly" ? "รายเดือน" : selection.billingCycle === "semi_annual" ? "ราย 6 เดือน" : "รายปี"}
                  </p>
                )}
              </div>
            </div>
            {currentStep === 2 ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          
          {currentStep === 2 && (
            <div className="p-4 md:p-6 pt-0 border-t">
              <div className="grid md:grid-cols-3 gap-4">
                {/* Monthly */}
                <button
                  onClick={() => setSelection({ ...selection, billingCycle: "monthly" })}
                  className={`relative p-4 md:p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    selection.billingCycle === "monthly"
                      ? "border-emerald-500 bg-emerald-50 shadow-lg"
                      : "border-gray-200 hover:border-emerald-300 hover:shadow-md"
                  }`}
                >
                  {selection.billingCycle === "monthly" && (
                    <div className="absolute -top-3 -right-3 bg-emerald-500 text-white p-2 rounded-full">
                      <Check className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  )}
                  
                  <h3 className="text-xl md:text-2xl font-bold text-textDark mb-2">รายเดือน</h3>
                  <p className="text-xs md:text-sm text-textLight mb-4">ชำระทุกเดือน</p>
                  
                  <div className="text-2xl md:text-3xl font-bold text-emerald-600">
                    ฿{selection.planType === "pro" ? "690" : "1,590"}
                  </div>
                </button>

                {/* Semi-Annual */}
                <button
                  onClick={() => setSelection({ ...selection, billingCycle: "semi_annual" })}
                  className={`relative p-4 md:p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    selection.billingCycle === "semi_annual"
                      ? "border-purple-500 bg-purple-50 shadow-lg"
                      : "border-gray-200 hover:border-purple-300 hover:shadow-md"
                  }`}
                >
                  {selection.billingCycle === "semi_annual" && (
                    <div className="absolute -top-3 -right-3 bg-purple-500 text-white p-2 rounded-full">
                      <Check className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  )}
                  
                  <h3 className="text-xl md:text-2xl font-bold text-textDark mb-2">ราย 6 เดือน</h3>
                  <p className="text-xs md:text-sm text-textLight mb-4">ฟรี 1 เดือน</p>
                  
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500 line-through">
                      ฿{(selection.planType === "pro" ? 690 * 6 : 1590 * 6).toLocaleString()}
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-purple-600">
                      ฿{(selection.planType === "pro" ? 690 * 5 : 1590 * 5).toLocaleString()}
                    </div>
                  </div>
                </button>

                {/* Yearly */}
                <button
                  onClick={() => setSelection({ ...selection, billingCycle: "yearly" })}
                  className={`relative p-4 md:p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    selection.billingCycle === "yearly"
                      ? "border-blue-500 bg-blue-50 shadow-lg"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                >
                  {selection.billingCycle === "yearly" && (
                    <div className="absolute -top-3 -right-3 bg-blue-500 text-white p-2 rounded-full">
                      <Check className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  )}
                  
                  <div className="absolute -top-2 left-4 bg-gradient-to-r from-blue-500 to-red-500 text-white px-3 py-1 rounded-full text-[10px] md:text-xs font-bold">
                    ฟรี 2 เดือน
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-bold text-textDark mb-2 mt-2">รายปี</h3>
                  <p className="text-xs md:text-sm text-textLight mb-4">คุ้มที่สุด!</p>
                  
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500 line-through">
                      ฿{(selection.planType === "pro" ? 690 * 12 : 1590 * 12).toLocaleString()}
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-blue-600">
                      ฿{(selection.planType === "pro" ? 690 * 10 : 1590 * 10).toLocaleString()}
                    </div>
                  </div>
                </button>
              </div>

              {isProPlusYearly && (
                <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-blue-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-blue-900 text-sm md:text-base">🎉 โปรโมชั่นพิเศษ Pro+ รายปี</h4>
                      <ul className="mt-2 space-y-1 text-xs md:text-sm text-blue-800">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span><strong>Subdomain ฟรี</strong> มูลค่า ฿1,188/ปี</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span><strong>Salepage ฟรี</strong> มูลค่า ฿1,788/ปี</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 3: URL Add-ons - HIDDEN */}
        {false && <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <button
            onClick={() => setCurrentStep(currentStep === 3 ? 0 : 3)}
            className="w-full p-4 md:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3 flex-1">
              <span className="w-8 h-8 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {(selection.hasPathUrl || selection.hasSubdomain) ? <Check className="w-5 h-5" /> : "3"}
              </span>
              <div className="text-left">
                <h2 className="text-lg md:text-xl font-bold text-textDark">
                  Step 3: เลือก URL Add-on {!isProPlusYearly && <span className="text-sm font-normal text-gray-500">(ไม่บังคับ)</span>}
                  {isProPlusYearly && <span className="text-sm font-normal text-blue-600">(บังคับ - Subdomain ฟรี)</span>}
                </h2>
                {(selection.hasPathUrl || selection.hasSubdomain) && currentStep !== 3 && (
                  <p className="text-sm text-gray-600">
                    {selection.hasPathUrl && "Path URL"}
                    {selection.hasPathUrl && selection.hasSubdomain && " + "}
                    {selection.hasSubdomain && "Subdomain"}
                  </p>
                )}
              </div>
            </div>
            {currentStep === 3 ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          
          {currentStep === 3 && (
            <div className="p-4 md:p-6 pt-0 border-t">
              <div className="space-y-3">
                {/* Path URL - Checkbox */}
                <label
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer block ${
                    selection.hasPathUrl
                      ? "border-cyan-500 bg-cyan-50 shadow-md"
                      : "border-gray-200 hover:border-cyan-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={selection.hasPathUrl}
                          onChange={(e) => setSelection({ ...selection, hasPathUrl: e.target.checked })}
                          className="w-5 h-5 text-cyan-600 rounded focus:ring-2 focus:ring-cyan-500"
                        />
                        <h3 className="text-base md:text-lg font-bold text-textDark">Path URL</h3>
                        <span className="text-xs md:text-sm font-bold text-cyan-600">
                          +฿{selection.billingCycle === "monthly" ? "49" : selection.billingCycle === "semi_annual" ? "245" : "490"}/
                          {selection.billingCycle === "monthly" ? "เดือน" : selection.billingCycle === "semi_annual" ? "6 เดือน" : "ปี"}
                        </span>
                      </div>
                      <p className="text-xs md:text-sm text-textLight ml-7">edumi.co/@username</p>
                      {selection.billingCycle === "semi_annual" && (
                        <p className="text-[10px] md:text-xs text-cyan-600 font-semibold mt-1 ml-7 flex items-center gap-1">
                          <Gift className="w-3 h-3" /> ฟรี 1 เดือน
                        </p>
                      )}
                      {selection.billingCycle === "yearly" && (
                        <p className="text-[10px] md:text-xs text-cyan-600 font-semibold mt-1 ml-7 flex items-center gap-1">
                          <Gift className="w-3 h-3" /> ฟรี 2 เดือน
                        </p>
                      )}
                    </div>
                  </div>
                </label>

                {/* Subdomain - Checkbox */}
                <label
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer block ${
                    selection.hasSubdomain
                      ? "border-blue-500 bg-blue-50 shadow-md"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={selection.hasSubdomain}
                          onChange={(e) => {
                            if (!isProPlusYearly) {
                              setSelection({ ...selection, hasSubdomain: e.target.checked });
                            }
                          }}
                          disabled={isProPlusYearly}
                          className={`w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${isProPlusYearly ? 'cursor-not-allowed opacity-70' : ''}`}
                        />
                        <h3 className="text-base md:text-lg font-bold text-textDark">Subdomain</h3>
                        {!isProPlusYearly && (
                          <span className="text-xs md:text-sm font-bold text-blue-600">
                            +฿{selection.billingCycle === "monthly" ? "99" : selection.billingCycle === "semi_annual" ? "495" : "990"}/
                            {selection.billingCycle === "monthly" ? "เดือน" : selection.billingCycle === "semi_annual" ? "6 เดือน" : "ปี"}
                          </span>
                        )}
                        {isProPlusYearly && (
                          <span className="text-xs md:text-sm font-bold text-green-600">ฟรี!</span>
                        )}
                      </div>
                      <p className="text-xs md:text-sm text-textLight ml-7">username.edumi.co</p>
                      {isProPlusYearly && (
                        <p className="text-[10px] md:text-xs text-blue-600 font-semibold mt-1 ml-7 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> รวมอยู่ใน Pro+ รายปีแล้ว
                        </p>
                      )}
                      {!isProPlusYearly && selection.billingCycle === "semi_annual" && (
                        <p className="text-[10px] md:text-xs text-blue-600 font-semibold mt-1 ml-7 flex items-center gap-1">
                          <Gift className="w-3 h-3" /> ฟรี 1 เดือน
                        </p>
                      )}
                      {!isProPlusYearly && selection.billingCycle === "yearly" && (
                        <p className="text-[10px] md:text-xs text-blue-600 font-semibold mt-1 ml-7 flex items-center gap-1">
                          <Gift className="w-3 h-3" /> ฟรี 2 เดือน
                        </p>
                      )}
                    </div>
                  </div>
                </label>
              </div>

              {/* Username Input - แสดงเมื่อเลือก URL Add-on ใดๆ */}
              {(selection.hasPathUrl || selection.hasSubdomain) && (
                <div className="mt-4">
                  <UsernameInput
                    value={selection.username}
                    onChange={(value) => setSelection({ ...selection, username: value })}
                    existingUsername={existingUsername}
                    isRenewal={false}
                    systemType="course"
                  />
                </div>
              )}

              {isProPlusYearly && (
                <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded text-xs md:text-sm">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-900">
                      <strong>Pro+ รายปี:</strong> คุณจะได้รับ Subdomain ฟรี! (แต่สามารถเลือก Path URL เพิ่มได้)
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}

          </div>

          {/* Right: Summary Sidebar (Sticky on desktop) */}
          <div className="lg:w-96 lg:sticky lg:top-8 lg:self-start">
            <div className="bg-gradient-to-br from-blue-600 to-pink-600 rounded-2xl shadow-2xl p-6 md:p-8 text-white">
          <h3 className="text-xl md:text-2xl font-bold mb-4">สรุปรายการ</h3>
          
          <div className="space-y-2 mb-6 text-sm md:text-base">
            {/* แพ็กเกจหลัก */}
            <div className="flex justify-between">
              <span>{selection.planType === "pro" ? "Pro" : "Pro+"} ({!selection.billingCycle ? "-" : selection.billingCycle === "monthly" ? "รายเดือน" : selection.billingCycle === "semi_annual" ? "6 เดือน" : "1 ปี"}):</span>
              <span className="font-bold">฿{getBasePrice().toLocaleString()}</span>
            </div>
            
            {/* URL Add-ons - HIDDEN */}
            
            {/* ส่วนลด */}
            {selection.billingCycle !== "monthly" && selection.billingCycle && getSavings() > 0 && (
              <>
                <div className="border-t border-white/30 pt-2 mt-2"></div>
                <div className="flex justify-between text-green-300">
                  <span className="flex items-center gap-1">
                    <Gift className="w-4 h-4" />
                    ส่วนลด (ฟรี {selection.billingCycle === "semi_annual" ? "1" : "2"} เดือน):
                  </span>
                  <span className="font-bold">-฿{getSavings().toLocaleString()}</span>
                </div>
              </>
            )}
            
            <div className="border-t border-white/30 pt-2 mt-2"></div>
            
            <div className="flex justify-between items-center text-lg md:text-2xl font-bold">
              <span>ราคารวม:</span>
              <span>฿{getTotalPrice().toLocaleString()}</span>
            </div>
          </div>

          <button
            onClick={handleContinue}
            className="w-full bg-white text-blue-600 py-3 md:py-4 rounded-xl font-bold text-base md:text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            ดำเนินการต่อ
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
